import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getJobQuestions, getCandidates, type JobQuestion, type ScreeningFilterSpec } from "../api";

function hasValue(spec?: ScreeningFilterSpec): boolean {
  if (!spec) return false;
  return !!(spec.in && spec.in.length) || spec.min !== undefined && spec.min !== "" ||
    spec.max !== undefined && spec.max !== "" || !!(spec.contains && spec.contains.trim());
}

// Lets a recruiter narrow a job's candidate list by the job's configured screening
// answers (Expected Salary, English Proficiency, etc. — see candidate-screening-questions
// spec AC7). Reports the matching application_ids up to the parent, which filters
// whatever it's already rendering (Kanban cards, table rows, ...); null = no active filter.
export default function ScreeningFilterBar({ jobId, onVisibleApplicationIdsChange }: {
  jobId: number;
  onVisibleApplicationIdsChange: (ids: Set<number> | null) => void;
}) {
  const [filters, setFilters] = useState<Record<number, ScreeningFilterSpec>>({});
  const { data: questions = [] } = useQuery({ queryKey: ["job-questions", jobId], queryFn: () => getJobQuestions(jobId) });

  const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => hasValue(v)));
  const activeCount = Object.keys(activeFilters).length;

  const { data: filteredRows } = useQuery({
    queryKey: ["candidates-screening-filter", jobId, activeFilters],
    queryFn: () => getCandidates(jobId, activeFilters as Record<number, ScreeningFilterSpec>),
    enabled: activeCount > 0,
  });

  useEffect(() => {
    if (activeCount === 0) { onVisibleApplicationIdsChange(null); return; }
    if (filteredRows) onVisibleApplicationIdsChange(new Set((filteredRows as any[]).map(r => r.application_id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCount, filteredRows]);

  if (questions.length === 0) return null;

  function setFilter(qid: number, spec: ScreeningFilterSpec | undefined) {
    setFilters(f => {
      const next = { ...f };
      if (!spec) delete next[qid]; else next[qid] = spec;
      return next;
    });
  }

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 14, padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>Filter by screening answers:</span>
      {questions.map((q: JobQuestion) => (
        <QuestionFilter key={q.id} question={q} value={filters[q.id]} onChange={spec => setFilter(q.id, spec)} />
      ))}
      {activeCount > 0 && (
        <button type="button" onClick={() => setFilters({})}
          style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
          Clear
        </button>
      )}
    </div>
  );
}

interface QuestionFilterProps {
  question: JobQuestion; value?: ScreeningFilterSpec; onChange: (v: ScreeningFilterSpec | undefined) => void;
}

const QuestionFilter: React.FC<QuestionFilterProps> = ({ question, value, onChange }) => {
  const fieldStyle: React.CSSProperties = { padding: "5px 8px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 12, color: "#334155" };

  if (question.field_type === "number") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#475569" }}>{question.label}:</span>
        <input type="number" placeholder="min" value={value?.min ?? ""} style={{ ...fieldStyle, width: 70 }}
          onChange={e => onChange({ ...value, min: e.target.value || undefined })} />
        <span style={{ color: "#94a3b8" }}>–</span>
        <input type="number" placeholder="max" value={value?.max ?? ""} style={{ ...fieldStyle, width: 70 }}
          onChange={e => onChange({ ...value, max: e.target.value || undefined })} />
      </div>
    );
  }
  if (question.field_type === "text") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <span style={{ fontSize: 12, color: "#475569" }}>{question.label}:</span>
        <input placeholder="contains…" value={value?.contains ?? ""} style={{ ...fieldStyle, width: 120 }}
          onChange={e => onChange(e.target.value ? { contains: e.target.value } : undefined)} />
      </div>
    );
  }

  const options = question.field_type === "yes_no" ? ["yes", "no"] : question.options;
  const selected = value?.in || [];
  function toggle(o: string) {
    const next = selected.includes(o) ? selected.filter(v => v !== o) : [...selected, o];
    onChange(next.length ? { in: next } : undefined);
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: "#475569" }}>{question.label}:</span>
      {options.map(o => (
        <label key={o} style={{
          display: "flex", alignItems: "center", gap: 4, fontSize: 11, cursor: "pointer",
          border: `1px solid ${selected.includes(o) ? "#00c9a7" : "#d1d5db"}`, borderRadius: 14,
          padding: "2px 8px", background: selected.includes(o) ? "#f0fdf4" : "white", color: "#334155",
        }}>
          <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} style={{ margin: 0, width: 11, height: 11 }} />
          {o === "yes" ? "Yes" : o === "no" ? "No" : o}
        </label>
      ))}
    </div>
  );
};
